/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
// Extends From
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
// Ember Imports
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { sort, setDiff } from '@ember/object/computed';
// External Imports
import config from 'linced-admin/config/environment';
import PageAccessLevel from '@linc-technologies/linced-common/enums/page-access-level';
import PageType from 'linced-admin/enums/page-type';
import ReactionType from '@linc-technologies/linced-common/enums/reaction-type';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { findBy, mapBy } from '@linc-technologies/linced-common/utils/arrays';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';
import {
  destroyAndSaveRelated,
  loadRelated,
} from 'linced-admin/utils/destroy-record';

export default class PageTemplatesController extends Controller {
  @service
  router;

  @action
  setBulkApplyTemplate(value) {
    this.bulkApplyTemplate = value;
  }

  @action
  setCanComment(value) {
    this.canComment = value;
  }

  @action
  setCaregiverDescription(value) {
    this.caregiverDescription = value;
  }

  @action
  setCaregiversCanPost(value) {
    this.caregiversCanPost = value;
  }

  @action
  setDescription(value) {
    this.description = value;
  }

  @action
  setHasAutogroup(value) {
    this.hasAutogroup = value;
  }

  @action
  setHasGoalsAsTags(value) {
    this.hasGoalsAsTags = value;
  }

  @action
  setHasLearnerIcon(value) {
    this.hasLearnerIcon = value;
  }

  @action
  setHasPosts(value) {
    this.hasPosts = value;
  }

  @action
  setIcon(value) {
    this.icon = value;
  }

  @action
  setIsAttendance(value) {
    this.isAttendance = value;
  }

  @action
  setIsSickbay(value) {
    this.isSickbay = value;
  }

  @action
  setLabel(value) {
    this.label = value;
  }

  @action
  setLearnerDescription(value) {
    this.learnerDescription = value;
  }

  @action
  setLearnersCanPost(value) {
    this.learnersCanPost = value;
  }

  @action
  setPostTemplate(value) {
    this.postTemplate = value;
  }

  @action
  setReactionType(value) {
    this.reactionType = value;
  }

  @action
  setStaffCanEdit(value) {
    this.staffCanEdit = value;
  }

  @action
  setStaffCanPublish(value) {
    this.staffCanPublish = value;
  }

  @action
  setTitle(value) {
    this.title = value;
  }

  @action
  setType(value) {
    this.type = value;
  }

  @action
  setVisibleInMenu(value) {
    this.visibleInMenu = value;
  }

  @action
  setVisibleOnContent(value) {
    this.visibleOnContent = value;
  }

  @action
  setVisibleOnStudentProfile(value) {
    this.visibleOnStudentProfile = value;
  }

  @action
  setVisibleToLearners(value) {
    this.visibleToLearners = value;
  }

  @action
  setVisibleToParents(value) {
    this.visibleToParents = value;
  }

  queryParams = ['currentFilter'];

  @service
  fetch;

  @service
  session;

  @service
  store;

  @service
  toaster;

  PageAccessLevel = PageAccessLevel;
  PageType = PageType;
  pageTypes = PageType.values;
  ReactionType = ReactionType;
  reactionTypes = ReactionType.values;
  @tracked currentFilter = PageType.STUDENT;

  @computed('filteredByTypePages.[]', 'selectedUsageSetsFilter.[]')
  get filteredPages() {
    return this.get('filteredByTypePages').filter((page) => {
      if (!isEmpty(this.selectedUsageSetsFilter)) {
        return this.selectedUsageSetsFilter.some((usageSet) =>
          page.get('usageSetsArray').includes(usageSet),
        );
      }
      return true;
    });
  }

  @computed('model.pages.@each.type', 'currentFilter')
  get filteredByTypePages() {
    return this.get('model.pages').filter((page) => {
      return page.type === this.currentFilter;
    });
  }

  // Sorting
  @tracked
  sortDialogOpen = false;

  pageSort = Object.freeze(['sequence', 'label']);

  @sort('model.pages', 'pageSort')
  sortedPages;

  @sort('filteredPages', 'pageSort')
  sortedFilteredPages;

  @tracked
  currentPage = null;
  @tracked
  pageId = '';
  @tracked
  pageLabel = '';
  pageKey = '';
  pageVisibleToParents = false;
  pageSiblingLinked = false;
  pageType = null;
  @tracked
  type = null;
  @tracked
  hasPosts = false;
  @tracked
  tagCategories = [];

  @setDiff('model.tagCategories', 'tagCategories')
  remainingTagCategories;

  @tracked
  hasGoals = false;
  @tracked
  hasGoalsAsTags = false;
  @tracked
  goalSubjects = [];

  @setDiff('model.goalSubjects', 'goalSubjects')
  remainingGoalSubjects;

  @computed('goalSubjects.length')
  get hasMultipleGoalSubjects() {
    return this.goalSubjects.length > 1;
  }

  sortingGoalSubjects = false;
  @tracked
  hasEffort = false;
  @tracked
  hasOTJAssessment = false;
  @tracked hasAssessments = false;
  @tracked
  assessments = [];

  @setDiff('model.assessments', 'assessments')
  remainingAssessments;

  @computed('hasAssessments', 'assessments.length')
  get hasMultipleAssessments() {
    return this.hasAssessments && this.assessments.length > 1;
  }

  sortingAssessments = false;

  // Is this a special use page
  // ie. sickbay
  get isSpecialUse() {
    return this.isSickbay || this.isAttendance;
  }

  get specialUseLabel() {
    switch (true) {
      case this.isSickbay:
        return 'Sickbay';
      case this.isAttendance:
        return 'Attendance';
      default:
        return '';
    }
  }

  // Page configuration
  @tracked
  icon = '';

  @tracked
  label = '';
  @tracked
  title = '';
  @tracked
  description = '';
  @tracked
  caregiverDescription = '';
  @tracked
  learnerDescription = '';
  @tracked
  postTemplate = '';
  @tracked
  reactionType = null;

  // Page visibility
  @tracked
  visibleInMenu = false;

  @tracked
  visibleOnContent = false;
  @tracked
  visibleOnStudentProfile = false;
  @tracked
  visibleToParents = false;
  @tracked
  visibleToLearners = false;

  // Page authorisation / permissions
  @tracked
  staffCanPublish = false;

  @tracked
  staffCanEdit = false;
  @tracked
  caregiversCanPost = false;
  @tracked
  learnersCanPost = false;
  @tracked
  canComment = false;

  // Page magic
  @tracked
  hasLearnerIcon = false;

  @tracked
  hasAutogroup = false;

  // Page Access Level
  @tracked accessLevel = PageAccessLevel.STANDARD;

  get hasRestrictedAccess() {
    // Disable page visibility flags for caregiver or learners for non-standard pages.
    return this.accessLevel !== PageAccessLevel.STANDARD;
  }

  accessLevelNoteMap = Object.freeze({
    [PageAccessLevel.STANDARD]:
      ' - only visible to staff members who have standard, sensitive, or confidential access. No access to staff members with restricted access.',
    [PageAccessLevel.SENSITIVE]:
      ' - only visible to staff members who have sensitive or confidential access.',
    [PageAccessLevel.CONFIDENTIAL]:
      ' - only visible to staff members who have confidential level access.',
  });

  // Is this a Sickbay page
  @tracked isSickbay = false;

  @tracked isAttendance = false;
  @tracked
  pageDialogOpen = false;
  @tracked
  saveInProgress = false;
  @tracked
  deletePageDialogOpen = false;
  @tracked
  deleteInProgress = false;
  @tracked
  errorMessage = '';

  // Usage sets attributes
  @tracked
  usageSetDialogOpen = false;

  @tracked
  selectedUsageSets = [];
  @tracked
  initialUsageSets = [];

  @setDiff('model.usageSets', 'selectedUsageSetsFilter')
  remainingUsageSetsFilter;

  @tracked
  selectedUsageSetsFilter = [];
  pageProperties = Object.freeze([
    'hasAssessments',
    'hasPosts',
    'hasGoals',
    'hasEffort',
    'icon',
    'label',
    'title',
    'description',
    'caregiverDescription',
    'learnerDescription',
    'postTemplate',
    'reactionType',
    'visibleInMenu',
    'visibleOnContent',
    'visibleOnStudentProfile',
    'visibleToParents',
    'visibleToLearners',
    'staffCanPublish',
    'staffCanEdit',
    'caregiversCanPost',
    'learnersCanPost',
    'canComment',
    'hasLearnerIcon',
    'hasAutogroup',
    'hasOTJAssessment',
    'hasGoalsAsTags',
    'accessLevel',
    'isSickbay',
    'isAttendance',
  ]);
  editorOptions = Object.freeze(defaultTinyMCEConfig());

  // Bulk Apply dialog.
  @tracked
  bulkApplyDialogOpen = false;

  @tracked
  bulkApplyInProgress = false;
  @tracked
  bulkApplyTemplate = null;
  @tracked
  bulkApplyGroup = null;
  @tracked
  bulkApplyExclusions = [];
  @tracked bulkApplyProcessed = 0;

  // remainingBulkApplySchools lists the group's schools that are not
  // excluded. schoolsArray is the loaded schools: group.schools is an async
  // hasMany (a PromiseManyArray), which is not an array.
  get remainingBulkApplySchools() {
    const excluded = new Set(this.bulkApplyExclusions);
    return (this.bulkApplyGroup?.schoolsArray ?? []).filter(
      (school) => !excluded.has(school),
    );
  }

  get bulkApplyProgress() {
    const total = this.bulkApplyGroup?.schoolsArray.length ?? 0;
    if (total === 0) {
      return 100;
    }
    if (this.bulkApplyProcessed < 1) {
      return 0;
    }
    return (this.bulkApplyProcessed / total) * 100;
  }

  init() {
    super.init(...arguments);
    this.resetForm();
  }

  // toggleDialog opens the page dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('pageDialogOpen');
    if (!this.pageDialogOpen) {
      this.resetForm();
    } else if (isEmpty(this.pageId)) {
      this.type = PageType.values.find((t) => t.value === this.currentFilter);
    }
  }

  @action
  filterChanged(newValue) {
    switch (newValue) {
      case 'tags':
        this.router.transitionTo('admin.tags');
        break;
      default:
        this.currentFilter = newValue;
        this.selectedUsageSetsFilter = [];
        break;
    }
  }

  @action
  togglePageDialog() {
    this.toggleDialog();
  }

  @action
  editPage(id) {
    this.store.findRecord('posts/page', id).then(async (page) => {
      this.setProperties(page.getProperties(this.pageProperties));
      this.type = findBy(this.pageTypes, 'value', page.get('type'));
      this.reactionType = findBy(
        this.reactionTypes,
        'value',
        page.get('reactionType'),
      );
      // Await the relations rather than reading the PromiseManyArrays: these
      // lists are saved back, so they must be fully loaded, not whatever has
      // resolved so far.
      let [goalSubjects, tagCategories, assessments] = await Promise.all([
        page.get('goalSubjects'),
        page.get('tagCategories'),
        page.get('assessments'),
      ]);
      this.setProperties({
        goalSubjects: goalSubjects.slice(),
        tagCategories: tagCategories.slice(),
        assessments: assessments.slice(),
      });
    });
    this.pageId = id;
    this.toggleDialog();
  }

  @action
  savePage() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';

    if (isEmpty(this.pageId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord(
          'posts/page',
          this.getProperties(this.pageProperties),
        );
        record.setProperties({
          authorId: this.get('session.personId'),
          type: this.get('type.value'),
          reactionType: this.get('reactionType.value'),
          goalSubjectIds: this.goalSubjects,
          tagCategoryIds: this.tagCategories,
          assessmentIds: this.assessments,
          sequence: 999,
          isSickbay: this.isSickbay,
          isAttendance: this.isAttendance,
        });
        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.pageId;
        this.store.findRecord('posts/page', id).then((page) => {
          page.setProperties(this.getProperties(this.pageProperties));
          page.setProperties({
            type: this.get('type.value'),
            reactionType: this.get('reactionType.value'),
            goalSubjectIds: this.goalSubjects,
            tagCategoryIds: this.tagCategories,
            assessmentIds: this.assessments,
            isSickbay: this.isSickbay,
            isAttendance: this.isAttendance,
          });

          resolve(page);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          if (action === 'create') {
            // Send an event to trigger the route to refresh the model
            this.router.refresh('page-templates');
          }

          this.pageDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          // The form still holds the edits for a retry; rollbackAttributes()
          // alone would leave goal subjects, tag categories and assessments.
          rollbackRecord(record);
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  @action
  deletePage(id) {
    this.errorMessage = '';
    this.store.findRecord('posts/page', id).then((page) => {
      this.pageLabel = page.get('label');
    });
    this.pageId = id;
    this.deletePageDialogOpen = true;
  }

  @action
  async confirmDelete(confirmed) {
    if (!confirmed) {
      this.deletePageDialogOpen = false;
      this.currentPage = null;
      return;
    }

    // Deletion confirmed; actually delete the record.
    this.deleteInProgress = true;
    this.errorMessage = '';

    // Grab record from local store - no GET request, as that causes weirdness.
    // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
    let page = this.store.peekRecord('posts/page', this.pageId);
    this.currentPage = page;

    // The page's usage sets get saved after the delete so the server drops
    // the page from them. Gather them first: after the delete the page's
    // relationships read as null (see destroyAndSaveRelated).
    const usageSets = await loadRelated(page, ['usageSetIds']);
    const { deleted, error } = await destroyAndSaveRelated(page, usageSets);
    this.deleteInProgress = false;
    if (!deleted) {
      this.handleError(error, 'delete');
      return;
    }
    if (error) {
      this.toaster.error(
        `The page was deleted, but removing it from its usage sets failed`,
      );
    }

    // Tidy up / close dialog.
    this.deletePageDialogOpen = false;
    this.currentPage = null;
    this.resetForm();
  }

  @action
  addGoalSubject(subject) {
    this.goalSubjects = [...this.goalSubjects, subject];
  }

  @action
  removeGoalSubject(subject) {
    this.goalSubjects = this.goalSubjects.filter((s) => s !== subject);
  }

  @action
  addTagCategory(category) {
    this.tagCategories = [...this.tagCategories, category];
  }

  @action
  removeTagCategory(category) {
    this.tagCategories = this.tagCategories.filter((c) => c !== category);
  }

  @action
  toggleHasAssessments() {
    this.toggleProperty('hasAssessments');
    if (this.hasAssessments) {
      // If page has assessments, set access level to default
      this.accessLevel = PageAccessLevel.STANDARD;
    }
  }

  @action
  addAssessment(assessment) {
    this.assessments = [...this.assessments, assessment];
  }

  @action
  removeAssessment(assessment) {
    this.assessments = this.assessments.filter((a) => a !== assessment);
  }

  @action
  setAccessBasedFlags(accessLevel) {
    this.accessLevel = accessLevel;
    if (accessLevel !== PageAccessLevel.STANDARD) {
      // If page access level is not Standard
      // Set following flags related to page visibility for caregiver or learners to false
      this.visibleOnStudentProfile = false;
      this.visibleToParents = false;
      this.visibleOnContent = false;
      this.visibleToLearners = false;
      this.caregiversCanPost = false;
      this.learnersCanPost = false;
    }
  }

  @action
  toggleBulkApplyDialog() {
    this.toggleProperty('bulkApplyDialogOpen');
    if (!this.bulkApplyDialogOpen) {
      this.bulkApplyInProgress = false;
      this.bulkApplyTemplate = null;
      this.bulkApplyGroup = null;
      this.bulkApplyExclusions = [];
      this.bulkApplyProcessed = 0;
    }
  }

  @action
  addBulkApplyExclusion(school) {
    this.bulkApplyExclusions = [...this.bulkApplyExclusions, school];
  }

  @action
  removeBulkApplyExclusion(school) {
    this.bulkApplyExclusions = this.bulkApplyExclusions.filter(
      (s) => s !== school,
    );
  }

  @action
  setBulkApplyGroup(group) {
    this.bulkApplyGroup = group;
    this.bulkApplyExclusions = [];
  }

  @action
  bulkApply() {
    this.bulkApplyInProgress = true;
    this.bulkApplyProcessed = 0;
    let excludedIds = mapBy(this.bulkApplyExclusions, 'id');
    let templateId = this.bulkApplyTemplate.id;
    this.bulkApplyGroup.schoolsArray.reduce((chain, school) => {
      return chain.then(() => {
        if (excludedIds.includes(school.id)) {
          this.incrementProperty('bulkApplyProcessed');
          return Promise.resolve();
        }
        return this.fetch
          .post(
            `${config.APP.apiHost}/posts/${config.APP.apiPath}/templates/pages/${templateId}/:apply`,
            {
              data: JSON.stringify({ schoolId: school.id }),
              headers: { 'X-Tenant-Id': school.id },
            },
          )
          .then((data) => {
            // Set the new Page's sequence to 999 to put it at the end of
            // the list, since the sequence won't necessarily align with the
            // school's existing Pages.
            data.page.sequence = 999;
            return this.fetch.put(
              `${config.APP.apiHost}/posts/${config.APP.apiPath}/pages/${data.page.id}`,
              { data },
            );
          })
          .then(() => this.incrementProperty('bulkApplyProcessed'));
      });
    }, Promise.resolve());
  }

  @action
  openSortDialog() {
    this.sortDialogOpen = true;
  }

  @action
  closeSortDialog() {
    this.sortDialogOpen = false;
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);
  }

  @action
  openUsageSetDialog(id) {
    let page = this.store.peekRecord('posts/page', id);
    page
      .get('usageSetIds')
      .then((usageSetsArray) => {
        this.usageSetDialogOpen = true;
        this.currentPage = page;
        this.selectedUsageSets = usageSetsArray.slice();
        this.initialUsageSets = usageSetsArray.slice();
      })
      .catch(() => {
        this.toaster.error(`Error trying to edit the page's usage sets`);
      });
  }

  @action
  closeUsageSetDialog() {
    // The pending save's callbacks still need the state this clears.
    if (this.saveInProgress) {
      return;
    }
    this.usageSetDialogOpen = false;
    this.selectedUsageSets = [];
    this.initialUsageSets = [];
    this.currentPage = null;
  }

  @action
  addUsageSet(usageSet, checked) {
    if (checked) {
      this.selectedUsageSets = [...this.selectedUsageSets, usageSet];
    } else {
      this.selectedUsageSets = this.selectedUsageSets.filter(
        (u) => u !== usageSet,
      );
    }
  }

  @action
  addUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = [...this.selectedUsageSetsFilter, usageSet];
  }

  @action
  removeUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = this.selectedUsageSetsFilter.filter(
      (u) => u !== usageSet,
    );
  }

  @action
  savePageAndUsageSet() {
    this.saveInProgress = true;
    let promises = [];
    let changedUsageSets = [];

    // Update the usage sets
    let removedUsageSets = this.initialUsageSets.filter(
      (usageSet) => !this.selectedUsageSets.includes(usageSet),
    );
    let addedUsageSets = this.selectedUsageSets.filter(
      (usageSet) => !this.initialUsageSets.includes(usageSet),
    );
    changedUsageSets = changedUsageSets.concat(
      removedUsageSets,
      addedUsageSets,
    );
    if (!isEmpty(changedUsageSets)) {
      // Usage sets a page has been newly added to/removed from
      changedUsageSets.forEach((changedUsageSet) => {
        let promise = changedUsageSet.save();
        promises.push(promise);
      });
    }

    // Save the page template at the end
    this.currentPage.set('usageSetIds', this.selectedUsageSets);
    promises.push(this.currentPage.save());

    return Promise.all(promises)
      .then(() => {
        // After saving the page and all involved usage sets
        this.toaster.success(`Successfully saved the page and usage sets`);
        this.currentPage.reload();
        this.usageSetDialogOpen = false;
        this.currentPage = null;
        this.selectedUsageSets = [];
        this.initialUsageSets = [];
        this.saveInProgress = false;
      })
      .catch(() => {
        this.toaster.error(`Error saving the page's usage sets`);
        // Put the usage sets back, which also reverts each usage set's side.
        // rollbackAttributes() does not touch relationships.
        this.currentPage.set('usageSetIds', this.initialUsageSets);
        this.currentPage.rollbackAttributes();
        this.saveInProgress = false;
      });
  }

  // unpublishPageTemplate will remove the page from all usage sets so it becomes unusable
  @action
  unpublishPageTemplate(page) {
    page.get('usageSetIds').then((usageSetsArray) => {
      if (!isEmpty(usageSetsArray)) {
        this.selectedUsageSets = [];
        this.initialUsageSets = usageSetsArray.slice();
        this.currentPage = page;
        this.savePageAndUsageSet();
      }
    });
  }

  handleError(err) {
    if (!isEmpty(err?.errors?.msg)) {
      this.errorMessage = err.errors.msg;
    } else {
      this.errorMessage = err?.message;
    }
  }

  resetForm() {
    this.pageId = '';
    this.type = { value: 1, label: 'Student' };
    this.hasAssessments = false;
    this.hasPosts = false;
    this.hasGoals = false;
    this.hasEffort = false;
    this.hasGoalsAsTags = false;
    this.hasOTJAssessment = false;
    this.goalSubjects = [];
    this.tagCategories = [];
    this.assessments = [];
    this.icon = '';
    this.label = '';
    this.title = '';
    this.description = '';
    this.caregiverDescription = '';
    this.learnerDescription = '';
    this.postTemplate = '';
    this.reactionType = { value: 1, label: 'None' };
    this.visibleInMenu = false;
    this.visibleOnContent = false;
    this.visibleOnStudentProfile = false;
    this.visibleToParents = false;
    this.staffCanPublish = false;
    this.staffCanEdit = false;
    this.caregiversCanPost = false;
    this.learnersCanPost = false;
    this.canComment = false;

    this.accessLevel = PageAccessLevel.values.find(
      (level) => level.value === PageAccessLevel.STANDARD,
    );

    this.bulkApplyDialogOpen = false;
    this.bulkApplyInProgress = false;
    this.bulkApplyTemplate = null;
    this.bulkApplyGroup = null;
    this.bulkApplyExclusions = [];
    this.bulkApplyProcessed = 0;
    this.isSickbay = false;
    this.isAttendance = false;
    this.selectedUsageSetsFilter = [];
  }

  reset() {
    this.resetForm();
  }
}
